import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, HostBinding, OnDestroy, Optional, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatNotification } from 'src/lib/modules/mat-notifications/mat-notifications.module';
import { MatStepper } from '@angular/material/stepper';
import { Router } from '@angular/router';
import { ApiService } from 'src/lib/services/api.service';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { MatIntro } from '@lib/modules/mat-intro/mat-intro.module';
import { ActivatedRoute } from '@lib/services/activated-route.service';
import { AppService } from '@lib/services/app.service';

@Component({
	selector: 'demande-new',
	templateUrl: './demande-new.component.html',
	styleUrls: ['./demande-new.component.scss'],
	providers: [{
		provide: STEPPER_GLOBAL_OPTIONS, useValue: {showError: true}
	}]
})
export class DemandeNewComponent implements AfterViewInit, OnDestroy {

    private subscriptions = new Subscription();

	@Output() onAdd: EventEmitter<any> = new EventEmitter();

	@ViewChild('demandeStepper') demandeStepperRef: MatStepper;

	types: Observable<any[]> = this.apiService.get('demandes/types/visibles', {shared: true});
	reasons: Observable<any[]> = this.apiService.get('demandes/default/reasons', {shared: true});
	importances: Observable<any[]> = this.apiService.get('demandes/default/importances', {shared: true}).pipe(
										map((importances: any[]) => importances.map(importance => ({
											...importance,
											canActivate: () => {

												if([3].includes(importance.id))
													return [1, 3].includes(this.demande.controls.steps?.get([1, 'reason_id']).value);
												else
													return true;
											}
										})))
									);

	demande = this.formBuilder.group({
		steps: this.formBuilder.array([
			this.formBuilder.group({
				type_id: [, Validators.required]
			}),
			this.formBuilder.group({
				reason_id: [, Validators.required],
				importance_id: [, Validators.required]
			}),
			this.formBuilder.group({
				actors: [],
				subject: [, Validators.required],
				description: [, Validators.required],
				attached: []
			})
		])
	});

    introState = this.intro.getState('demandes-new');
    introReplay = this.intro.need('demandes-new', () => import('../../guides/demandes-new.guide').then(m => m.default));

    isFramed = this.appService.isFramed

    @HostBinding('class.is-first-step-locked') isFirstStepLocked = false;
    @HostBinding('class.is-second-step-locked') isSecondStepLocked = false;

	constructor(private window: Window,
                private apiService: ApiService,
                private appService: AppService,
				private formBuilder: UntypedFormBuilder,
				private router: Router,
				private notification: MatNotification,
				@Optional() public dialogRef: MatDialogRef<DemandeNewComponent>,
                private activatedRoute: ActivatedRoute,
                private intro: MatIntro,
                private changeDetectorRef: ChangeDetectorRef) {

        this.subscriptions.add(
            this.activatedRoute.params.subscribe(params => {
                
                if(params.type) {
                    this.isFirstStepLocked = true;
                    this.demande.controls.steps.get([0]).setValue({type_id: params.type});
                }
                
                if(params.reason && params.importance) {
                    this.isSecondStepLocked = true;
                    this.demande.controls.steps.get([1]).setValue({reason_id: params.reason, importance_id: params.importance});
                }
            })
        );
    }

    ngAfterViewInit(): void {
        this.validateAndNext();
    }

	validateAndNext () {
		if(this.demande.controls.steps?.get([this.demandeStepperRef.selectedIndex]).valid) {
			this.demandeStepperRef.next();
            this.validateAndNext();
            this.changeDetectorRef.detectChanges();
        } else
            return false;
	}

	resetImportances () {
		return this.demande.controls.steps?.get([1, 'importance_id']).reset()
	}

	setActors (actors: any[]) {
		return this.demande.controls.steps?.get([2, 'actors']).patchValue(actors);
	}

	add () {
		if(this.demande.valid) {

            if(this.dialogRef)
			    this.dialogRef.close();

			const notificationRef = this.notification.open({message: 'Envoi de la demande...', progress: 'indeterminate'});
			const demande = {
				...this.demande.value.steps[0],
				...this.demande.value.steps[1],
				...this.demande.value.steps[2]
			};

			this.apiService.post('demandes', new FormData().from(demande)).subscribe(demande => {
                if(this.isFramed) {
                    notificationRef.close();
                    this.router.navigate(['demandes/new/done'], {replaceUrl: true});
                } else {
                    notificationRef.set({type: 'success', message: 'Demande envoyée', canClose: true, actions: [
                        {text: 'Voir la demande', action: () => this.router.navigate(['demandes', demande.id])}
                    ]});
                    this.onAdd.emit(demande);
                }
			});
		}
	}

    onPaste (event: ClipboardEvent) {

		const attached: File[] = [ 
			...Array.from(this.demande.controls.steps?.get([2, 'attached']).value ?? []),
			...(event.clipboardData || (<any> this.window).clipboardData).files
		];

		this.demande.controls.steps?.get([2]).patchValue({attached});
	}

    ngOnDestroy(): void {
        
        this.subscriptions.unsubscribe();
    }
}
